/**
 * AppLayout - Persistent sidebar navigation layout for dao-suite.
 *
 * Story BL-049.1 - Portal suite sidebar navigation
 * Pattern: matches think-tank-suite AppLayout (header + sidebar + scrollable content)
 *
 * AC1: 8 nav items with lucide-react icons
 * AC2: Active route highlighting with bg-primary text-primary-foreground
 * AC3: Header bar with title, NotificationBell, SuiteSwitcher
 * AC4: Logout button clears cookies, stores, localStorage, redirects
 * AC5: Independent scrolling — sidebar fixed, main content scrolls
 */
import { Outlet, NavLink, Navigate, useLocation, Link } from 'react-router-dom';
import { LogOut, UserPlus } from 'lucide-react';
import { buildNavigation } from './navigation';
import { SuiteSwitcher } from '@hello-world-dao/ui';
import { useRoles, useOnboardingGate } from '@hello-world-dao/auth';
import { NotificationBell } from '@/components/NotificationBell';
import { RecoveryNudge } from '@/components/RecoveryNudge';
import { SwitchToFosButton } from '@/components/layout/SwitchToFosButton';
import { clearTokenBalance, clearTreasury, clearBurnPool, clearEscrow } from '@/stores';
import { logout as cookieLogout } from '@/services/authCookieClient';
import { clearIdentityJwt } from '@/services/identity-jwt-store';
import { logoutIdentitySession, buildLoginFrontdoorUrl } from '@/services/identity-service-client';
import {
  isPropertyGraduationViewEnabled,
  isSubmissionCaptureEnabled,
  isCredentialStatusEnabled,
} from '@/utils/featureFlags';
import { useMembership } from '@/hooks/useMembership';


export function AppLayout() {
  /** BL-055 AC2/AC3/AC4: Filter nav items by role */
  const { hasRole, isAdmin } = useRoles();

  /**
   * bl-1066: the DAO-join on-ramp. A logged-in NON-member (not yet Active) is
   * offered "Join the DAO" as an identity-level choice in the sidebar footer,
   * beside Logout (dao-suite has no avatar dropdown; the sidebar is the account
   * menu). Gated on `useMembership()` — NOT roles (every session ships
   * `roles:['member']`, which gates nothing). Destination `/membership` = the
   * logged-in non-member join (first-year $50 card), NOT `/membership/renewal`.
   * Menley delivered the placement + copy (MENLEY-RESPONSE-2026-10-30 §for-Coby);
   * component choice is Coby's per her render note. Hidden while membership loads
   * (fail-closed: no flash) and for Active members.
   */
  const { isActiveMember, isLoading: isMembershipLoading } = useMembership();

  /**
   * onb-001-10 LOOP FIX (AC4). An authenticated member with NO `ic_principal`
   * (never crossed into the DAO) must link their Internet Identity BEFORE any
   * member-data page renders. Without this, the member-data calls inside the
   * portal pages (Dashboard etc.) 401 on the missing principal and bounce to the
   * login front-door — an infinite loop, because they ARE authenticated and just
   * land back here.
   *
   * The fix: redirect a `needsOnboarding` member to the chromeless `/onboarding`
   * crossing BEFORE rendering `<Outlet/>` (so no member-data call ever fires). The
   * `/onboarding` route lives OUTSIDE this layout, so reaching it ends the redirect
   * — there is no loop. `useOnboardingGate` is fail-closed while auth loads
   * (`needsOnboarding === false`), so this never decides mid-load and never flashes
   * the portal.
   *
   * Guard against an in-layout loop: never redirect when already under the
   * onboarding flow (`/onboarding` itself is not in this layout, but Step-2
   * `/onboarding/profile` IS — and it owns its own II-link-first gate, so a member
   * mid-profile must not be yanked back to Step-1 by this layout).
   *
   * bl-795 — VERIFY-EMAIL FIRST. A logged-in member whose email is NOT verified
   * (`needsEmailVerification`) is routed to the chromeless `/onboarding/verify-email`
   * step BEFORE the II-link crossing. oracle-bridge member-data rejects a bearer
   * whose `email_verified` is explicitly false, so sending an unverified member to
   * II-link → profile dead-ends on a 401 (the silent-gate trap). This check runs
   * AHEAD of the `needsOnboarding` redirect so verify-email precedes II-link. It
   * mirrors the same fail-closed/`underOnboarding` discipline — `/onboarding/verify-email`
   * is itself under the onboarding prefix, so it's exempt and there's no loop/flash.
   */
  const { needsOnboarding, needsEmailVerification } = useOnboardingGate();
  const location = useLocation();
  const underOnboarding = location.pathname.startsWith('/onboarding');
  if (needsEmailVerification && !underOnboarding) {
    return <Navigate to="/onboarding/verify-email" replace />;
  }
  if (needsOnboarding && !underOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  const navigation = buildNavigation({
    propertyGraduation: isPropertyGraduationViewEnabled(),
    submissionCapture: isSubmissionCaptureEnabled(),
    credentialStatus: isCredentialStatusEnabled(),
  });

  const visibleNavItems = navigation.filter((item) => {
    if (!item.requiredRole) return true;
    if (item.requiredRole === 'member') return hasRole('member') || isAdmin;
    return hasRole(item.requiredRole);
  });

  /**
   * AC4: Logout handler — revoke BOTH sessions (the legacy oracle-bridge cookie
   * AND the identity-service refresh-token family + httpOnly cookie), clear the
   * in-memory identity JWT + member stores + localStorage, then redirect to the
   * hosted login front-door. Best-effort — the redirect proceeds regardless
   * (uid-001-suite-login-frontdoor).
   */
  async function handleLogout() {
    // Best-effort: a failed cookie logout must NOT abort the rest of the cleanup
    // (identity-session revoke, JWT clear, store/localStorage clear, redirect).
    try {
      await cookieLogout();
    } catch {
      // continue — local clear + redirect proceed regardless
    }
    // Revoke the identity-service refresh family + clear its httpOnly cookie, and
    // drop the in-memory access JWT so no stale bearer survives the redirect.
    // (logoutIdentitySession is itself best-effort — swallows its own errors.)
    await logoutIdentitySession();
    clearIdentityJwt();
    localStorage.removeItem('user_data');
    clearTokenBalance();
    clearTreasury();
    clearBurnPool();
    clearEscrow();
    // dao-suite has no login page — redirect to the hosted login front-door.
    window.location.href = buildLoginFrontdoorUrl();
  }

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* AC3: Header bar — title + NotificationBell + SuiteSwitcher */}
      <header className="h-16 border-b border-border bg-card flex items-center justify-between px-6">
        <h1 className="text-xl font-bold">Hello World DAO</h1>
        <div className="flex items-center gap-4">
          <NotificationBell />
          {/* PLATFORM-003.2 — cross-domain nav to FounderyOS */}
          <SwitchToFosButton />
          <SuiteSwitcher
            currentSuite="portal"
            suiteUrls={{
              // Think Tank is deprecated (unified under FounderyOS); the old
              // think-tank host is dead. Point this entry at the FounderyOS
              // dashboard directly. (The SuiteSwitcher hides any entry whose
              // href is undefined, so an unset VITE_FOS_DASHBOARD_URL degrades
              // gracefully to a hidden item rather than a dead link.)
              thinkTank: import.meta.env.VITE_FOS_DASHBOARD_URL,
              governance: import.meta.env.VITE_GOVERNANCE_SUITE_URL,
              otterCamp: import.meta.env.VITE_OTTER_CAMP_URL,
              admin: import.meta.env.VITE_ADMIN_SUITE_URL,
            }}
          />
        </div>
      </header>

      {/* AC5: Independent scrolling — sidebar fixed, main scrolls */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="flex w-48 flex-col border-r border-border bg-card">
          {/* Sidebar nav header */}
          <div className="flex h-16 items-center px-6 border-b border-border">
            <div className="text-xl font-bold">Navigation</div>
          </div>

          {/* AC1+AC2: Nav items with active/inactive styling */}
          <nav className="flex-1 p-4 space-y-1">
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`
                }
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </NavLink>
            ))}
          </nav>

          {/* bl-1066 + AC4: sidebar footer — DAO-join on-ramp (non-members) + Logout */}
          <div className="p-4 border-t border-border">
            {!isMembershipLoading && !isActiveMember && (
              <Link
                to="/membership"
                title="Join the DAO and own a share of what you help build."
                className="flex w-full items-center gap-3 px-3 py-2 mb-2 rounded-md text-sm font-medium text-primary transition-colors hover:bg-primary/10"
              >
                <UserPlus className="h-5 w-5" />
                Join the DAO
              </Link>
            )}
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
            >
              <LogOut className="h-5 w-5" />
              Logout
            </button>
          </div>
        </aside>

        {/* Main content — scrolls independently */}
        <main className="flex-1 overflow-auto">
          {/* onb-001-3-fe ③: portal-entry persistent lost-II recovery nudge.
              Recurring, dismissible, NON-blocking; ships DARK behind the flag
              (renders nothing when off). */}
          <RecoveryNudge />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
